import type { MedicationStatementDto } from "@repo/data-store";
import type { DoseFrequency, DoseUnit, MedicationDosage, MedicationRoute } from "@repo/domain";
import { dosageSentence, FREQUENCY_OPTIONS } from "@/shared/lib/medication-dosage";

/** What a clinician types for a frequency, mapped to the closed list the record keeps. */
const FREQUENCY_ALIASES: Readonly<Record<string, DoseFrequency>> = {
  qd: "daily",
  daily: "daily",
  "once daily": "daily",
  "once a day": "daily",
  bid: "twice-daily",
  "twice daily": "twice-daily",
  "twice a day": "twice-daily",
  tid: "three-times-daily",
  "three times daily": "three-times-daily",
  "three times a day": "three-times-daily",
  qid: "four-times-daily",
  "four times daily": "four-times-daily",
  "four times a day": "four-times-daily",
  q4h: "every-4-hours",
  "every 4 hours": "every-4-hours",
  q6h: "every-6-hours",
  "every 6 hours": "every-6-hours",
  q8h: "every-8-hours",
  "every 8 hours": "every-8-hours",
  qod: "every-other-day",
  "every other day": "every-other-day",
  qhs: "at-bedtime",
  "at bedtime": "at-bedtime",
  nightly: "at-bedtime",
  weekly: "weekly",
  monthly: "monthly",
};

export const FREQUENCY_CHOICES = FREQUENCY_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
  keywords: Object.entries(FREQUENCY_ALIASES)
    .filter(([, frequency]) => frequency === option.value)
    .map(([alias]) => alias),
}));

const UNIT_ALIASES: Readonly<Record<string, DoseUnit>> = {
  tab: "tablet",
  tabs: "tablet",
  tablet: "tablet",
  tablets: "tablet",
  cap: "capsule",
  caps: "capsule",
  capsule: "capsule",
  capsules: "capsule",
  mg: "mg",
  mcg: "mcg",
  g: "g",
  ml: "mL",
  drop: "drop",
  drops: "drop",
  gtt: "drop",
  gtts: "drop",
  puff: "puff",
  puffs: "puff",
  patch: "patch",
  patches: "patch",
  application: "application",
  applications: "application",
  unit: "unit",
  units: "unit",
  u: "unit",
};

/**
 * "1 tab" becomes an amount the record can hold; anything else ("1 scoop",
 * "as directed") is kept as the dose's own words rather than refused.
 */
export function parseDose(typed: string): { amount?: MedicationDosage["amount"]; text?: string } {
  const value = typed.trim();
  if (!value) return {};
  const match = /^(\d+(?:\.\d+)?)\s*([a-z]+)$/i.exec(value);
  const unit = match?.[2] && UNIT_ALIASES[match[2].toLowerCase()];
  const amount = match ? Number(match[1]) : Number.NaN;
  if (unit && amount > 0) return { amount: { value: amount, unit } };
  return { text: value };
}

/** "1 tablet" back to what the dose pill shows. */
export function doseLabel(amount: MedicationDosage["amount"]): string {
  if (!amount) return "";
  return `${amount.value} ${amount.unit}`;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** MM/DD/YYYY to FHIR's date, when it is a real day no later than today. */
export function parseStopDate(typed: string): string | undefined {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(typed.trim());
  if (!match) return undefined;
  const date = `${match[3]}-${match[1]}-${match[2]}`;
  return isPastDate(date) ? date : undefined;
}

/** FHIR's date back to MM/DD/YYYY; a year or month alone has no day to show. */
export function stopDateInput(date: string | undefined): string | undefined {
  const match = date && /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return match ? [match[2], match[3], match[1]].join("/") : undefined;
}

/** A real calendar date, at whatever precision, no later than today. */
function isPastDate(date: string): boolean {
  const [year, month, day] = date.split("-").map(Number);
  if (year === undefined) return false;
  if (month !== undefined && (month < 1 || month > 12)) return false;
  if (day !== undefined && (day < 1 || day > new Date(year, month ?? 1, 0).getDate())) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return date <= today.slice(0, date.length);
}

/** "2025-02" → "Feb 2025", at the precision it was recorded. */
export function stopDateLabel(date: string | undefined): string | undefined {
  if (!date) return undefined;
  const [year, month, day] = date.split("-");
  const name = month ? MONTHS[Number(month) - 1] : undefined;
  if (!name) return year;
  const monthLabel = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  return day ? `${monthLabel} ${Number(day)}, ${year}` : `${monthLabel} ${year}`;
}

const FORM_ROUTES: ReadonlyArray<[RegExp, MedicationRoute]> = [
  [/\((oral [^)]*|chewable|disintegrating[^)]*|sublingual)\)/i, "oral"],
  [/\(nasal[^)]*\)/i, "nasal"],
  [/\((topical|cream|ointment|gel|lotion)[^)]*\)/i, "topical"],
  [/\(transdermal[^)]*\)/i, "transdermal"],
  [/\(inhal[^)]*\)/i, "inhaled"],
  [/\(ophthalmic[^)]*\)/i, "ophthalmic"],
  [/\(otic[^)]*\)/i, "otic"],
  [/\(rectal[^)]*\)/i, "rectal"],
  [/\(vaginal[^)]*\)/i, "vaginal"],
];

/** The route an RxTerms name already says ("Spironolactone (Oral Pill)"), so it is never asked again. */
export function routeFromForm(name: string): MedicationRoute | undefined {
  return FORM_ROUTES.find(([pattern]) => pattern.test(name))?.[1];
}

/**
 * An RxTerms display reads like a catalogue line: "ELIQUIS (Oral Pill) 5 mg Tab".
 * In a sentence it should read "Eliquis 5 mg".
 */
export function proseDrugName(display: string): string {
  const withoutForm = display
    .replace(/\s*\([^)]*\)/g, "")
    .replace(
      /\s+(Tab|Cap|Chewable Tab|Oral Solution|Oral Suspension|Injection|Solution|Suspension|Susp|Soln|Patch)s?$/i,
      "",
    )
    .trim();
  return withoutForm
    .replace(/\b[A-Z][A-Z0-9-]{2,}\b/g, (word) => `${word.charAt(0)}${word.slice(1).toLowerCase()}`)
    .replace(/\b(?=[A-Za-z]*[a-z])(?=[A-Za-z]*[A-Z]{3})[A-Za-z]+\b/g, (word) => {
      // Tall-man lettering ("metFORMIN", "hydroCHLOROthiazide") marks look-alike
      // drugs on a pick list; in a sentence it reads as shouting.
      const lower = word.toLowerCase();
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    });
}

/** The phrase's sentence, read off the live record. */
export function medicationSentence(medication: MedicationStatementDto): string {
  const name = medicationName(medication);
  const dose = medication.dosageText ?? dosageSentence(medication.dosage);
  const { amount, frequency, asNeeded, route } = medication.dosage ?? {};
  const structured = dosageSentence(medication.dosage);
  // A dose of route alone reads as part of the verb: "takes Eliquis by mouth".
  const routeOnly =
    Boolean(route) && !amount && !frequency && !asNeeded && Boolean(structured) && dose?.startsWith(structured ?? "");
  const doseClause = dose ? `${routeOnly ? " " : ", "}${dose}` : "";
  switch (medication.status) {
    case "stopped":
    case "completed": {
      const stopped = stopDateLabel(medication.effectivePeriod?.end);
      return `Pt previously took ${name}${doseClause}${stopped ? `, stopped ${stopped}` : ""}.`;
    }
    case "on-hold":
      return `Pt has paused ${name}${doseClause}.`;
    case "intended":
      return `Pt is to start ${name}${doseClause}.`;
    default:
      return `Pt takes ${name}${doseClause}.`;
  }
}

export function medicationName(medication: Pick<MedicationStatementDto, "medication">): string {
  const label = medication.medication?.text ?? medication.medication?.display;
  return label ? proseDrugName(label) : "a medication";
}
